import { useMemo } from 'react';

import { orderBy } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useMutationCreateUserGroup } from 'api/mutations/createUserGroup.mutation';
import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';
import { useQueryGetRolesList } from 'api/queries/getRolesList.query';

import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { Button } from 'components/DesignSystem/Button';
import { Select } from 'components/DesignSystem/Select';
import { MenuListSelectAll } from 'components/DesignSystem/Select/components/MenuListSelectAll/MenuListSelectAll';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import classes from 'components/Settings/Components/UserGroups/components/userGroups.module.scss';
import { mapper, projectHierarchyMapper } from 'components/Settings/Components/UserGroups/userGroups.utils';
import { IUserGroupForm } from 'components/Settings/Components/UserGroups/userGroups.utils.type';
import { CustomizedFormErrorMessage } from 'components/common';

export const UserGroupsCreate = () => {
    const { data: rolesList } = useQueryGetRolesList();
    const { data: projectHierarchyList } = useQueryGetProgramGeneralHierarchyList('TreeProgramFilter');
    const { customerId } = useUserHook();
    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
    } = useForm<IUserGroupForm>();
    const { mutate, isLoading } = useMutationCreateUserGroup();
    const { handleSnackBar } = useCustomSnackBar();
    const history = useHistory();

    const handleSave = (data: IUserGroupForm) => {
        if (!customerId) return null;
        mutate(
            {
                customer_id: customerId,
                group_name: data.group_name.trim(),
                projects_list: data.projects_list.map((item) => item.value),
                roles_list: data.roles_list.map((item) => item.value),
            },
            {
                onSuccess: () => {
                    handleSnackBar('Successfully created!', 'success');
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    history.goBack();
                },
            },
        );
    };

    const handleCancel = () => {
        history.goBack();
    };

    const rolesOptions = useMemo<ISelectOption<number>[]>(() => {
        return rolesList
            ? orderBy(rolesList.map(mapper), [(item: ISelectOption<number>) => item['label'].toLowerCase()], ['asc'])
            : [];
    }, [rolesList]);

    const projectsOptions = useMemo<ISelectOption<number>[]>(() => {
        return projectHierarchyList
            ? orderBy(
                  projectHierarchyList.children.map(projectHierarchyMapper),
                  [(item: ISelectOption<number>) => item['label'].toLowerCase()],
                  ['asc'],
              )
            : [];
    }, [projectHierarchyList]);

    return (
        <WidgetWithTitle title={`User Groups - New`}>
            <form onSubmit={handleSubmit(handleSave)}>
                <div className={classes.container}>
                    <div className={classes.row}>
                        <label className={classes.label}>Group Name</label>
                        <div className={classes.inputWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        id={'usergroups-groupName'}
                                        className={classes.input}
                                        placeholder={'Enter group name...'}
                                        maxLength={60}
                                        onClear={() => {
                                            setValue('group_name', '');
                                        }}
                                    />
                                )}
                                defaultValue={''}
                                name={'group_name'}
                                control={control}
                                rules={{
                                    validate: {
                                        min: (v) => v.length > 2 || 'must be at least 2 characters',
                                        max: (v) => v.length < 60 || 'must be no more than 60 characters',
                                    },
                                }}
                            />
                            {errors.group_name && <CustomizedFormErrorMessage text={errors.group_name.message} />}
                        </div>
                    </div>
                    <div className={classes.row}>
                        <label className={classes.label}>Roles</label>
                        <div className={classes.inputWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <Select<ISelectOption<number>, true>
                                        {...field}
                                        options={rolesOptions}
                                        isClearable={true}
                                        isMulti
                                        id={'usergroups-roles'}
                                    />
                                )}
                                defaultValue={[]}
                                name={'roles_list'}
                                control={control}
                                rules={{
                                    validate: {
                                        required: (value) => value?.length > 0 || 'Must select at least one role',
                                    },
                                }}
                            />
                            {errors.roles_list && <CustomizedFormErrorMessage text={errors.roles_list.message} />}
                        </div>
                    </div>
                    <div className={classes.row}>
                        <label className={classes.label}>Projects</label>
                        <div className={classes.inputWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <Select<ISelectOption<number>, true>
                                        {...field}
                                        options={projectsOptions}
                                        isClearable={true}
                                        isMulti
                                        id={'usergroups-projects'}
                                        components={{
                                            MenuList: MenuListSelectAll,
                                        }}
                                    />
                                )}
                                defaultValue={[]}
                                name={'projects_list'}
                                control={control}
                                rules={{
                                    validate: {
                                        required: (value) => value.length > 0 || 'Must select at least one project',
                                    },
                                }}
                            />

                            {errors.projects_list && <CustomizedFormErrorMessage text={errors.projects_list.message} />}
                        </div>
                    </div>
                    <div className={classes.row}>
                        <div className={classes.actionWrapper}>
                            <Button type={'submit'} isLoading={isLoading} disabled={isLoading}>
                                Save
                            </Button>
                            <Button onClick={handleCancel} variant={'secondary'}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
        </WidgetWithTitle>
    );
};
